/**
 * The local model catalog behind /docs/local-models.
 *
 * web/src/data/local-models.json is a snapshot of the browser (WASM/WebGPU)
 * model catalog as of Sokuji v0.42.1: each model's manifest entry joined with its
 * upstream and license, traced for kizuna-ai-lab/sokuji#584. Nothing regenerates
 * it. When a release adds, removes or changes a local model, edit the JSON by
 * hand, appVersion included; local-models.test.ts checks the values the page
 * renders. The export that produced it is in the closed kizuna-ai-lab/sokuji#589.
 */

import catalog from '@/data/local-models.json';

/** Section order on the page. */
export const MODEL_TYPES = ['asr', 'asr-stream', 'translation', 'tts', 'punctuation'] as const;
export const TIERS = ['open', 'conditional', 'non-commercial', 'unstated', 'cloud'] as const;
export const FLAGS = ['non-commercial-base', 'removed-upstream', 'game-character'] as const;
export const RUNTIMES = ['wasm', 'webgpu', 'cloud'] as const;

export type ModelType = (typeof MODEL_TYPES)[number];
export type LicenseTier = (typeof TIERS)[number];
export type ProvenanceFlag = (typeof FLAGS)[number];
export type Runtime = (typeof RUNTIMES)[number];

export interface LocalModelVariant {
  dtype: string;
  bytes: number;
  requiresShaderF16: boolean;
}

export interface LocalModel {
  id: string;
  name: string;
  type: ModelType;
  languages: string[];
  multilingual: boolean;
  sourceLang?: string;
  targetLang?: string;
  runtime: Runtime;
  streaming: boolean;
  variants: LocalModelVariant[];
  upstream: string;
  upstreamUrl?: string;
  revision?: string;
  via?: string;
  viaUrl?: string;
  license: string;
  licenseUrl?: string;
  tier: LicenseTier;
  flags?: ProvenanceFlag[];
  note?: string;
}

export interface BundledComponent {
  id: string;
  upstream: string;
  upstreamUrl?: string;
  license: string;
  licenseUrl?: string;
}

// The JSON's inferred types widen the unions to string; local-models.test.ts
// checks every value against the lists above.
export const LOCAL_MODELS = catalog.models as LocalModel[];
export const BUNDLED_COMPONENTS = catalog.bundled as BundledComponent[];
export const CATALOG_VERSION: string = catalog.appVersion;

/** The size the app's model list shows for the same download: whole mebibytes, labelled MB. */
export function formatBytes(bytes: number): string {
  return `${Math.round(bytes / 1_048_576)} MB`;
}

/** The languages column, or null for a multilingual model with no list, which the page names in words. */
export function languageLabel(model: Pick<LocalModel, 'languages' | 'multilingual' | 'sourceLang' | 'targetLang'>): string | null {
  if (model.sourceLang && model.targetLang) return `${model.sourceLang} → ${model.targetLang}`;
  if (model.languages.length > 0) return model.languages.join(', ');
  return null;
}
